export interface ClientTag {
  id: string;
  name: string;
  color: string;
}

export interface TenantTag extends ClientTag {
  clientCount: number;
}

export type SortOption = "name_asc" | "name_desc" | "created_asc" | "created_desc" | "updated_asc" | "updated_desc" | "industry_asc" | "industry_desc" | "tags_asc" | "tags_desc";

export interface Client {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  industry: string | null;
  businessGoals: string | null;
  notes: string | null;
  logoUrl: string | null;
  demoMode: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: ClientTag[];
  platforms?: string[];
  // IANA timezone (migration 0049, NOT NULL default 'UTC'). The clients GET
  // (select *) returns it at runtime; optional here so other Client constructors
  // (demo/mocks) need not set it. Used to default a new schedule's timezone (7.2).
  timezone?: string | null;
  // Owning Brand (Story 40-2 / Epic 40). brandId is the raw FK; brand is the
  // server-joined {id,name} summary the clients list/detail attach for the badge.
  // Both optional so demo/mock Client constructors need not set them.
  brandId?: string | null;
  brand?: { id: string; name: string } | null;
}

// Placeholder for Epic 4 — Integration Framework
export interface ClientIntegration {
  id: string;
  type: string; // 'google-analytics-4', 'meta-ads', etc.
  accountName: string | null;
  /**
   * Story 32-4: the integration's catalog link (oauth_grant_resource_id) —
   * the Remap picker excludes the currently-mapped resource. Null for
   * demo-mode rows.
   */
  oauthGrantResourceId: string | null;
  lastSyncAt: string | null;
  /**
   * Full 7-value union mirroring SYNC_STATUS_VALUES
   * (packages/db/src/schema/integrations.ts) — the server column carries all
   * of these at runtime; the old 4-value type was a lie that hid
   * `resource_unavailable` / `auth_failed` / `paused` (Story 32-4 AC #4).
   */
  syncStatus:
    | "pending"
    | "syncing"
    | "success"
    | "failed"
    | "auth_failed"
    | "resource_unavailable"
    | "paused";
  tokenStatus: "healthy" | "expiring_soon" | "expired" | "revoked" | "refresh_failed";
  consecutiveFailures?: number; // AC#2: yellow >= 2, red >= 5
}

// One entry per FAILED OAuth token from
// GET /api/clients/:clientId/integrations/failed-tokens (Story 32-5).
// Client-side mirror of the server's FailedTokenSummary
// (apps/server/src/routes/clients/integrations.ts) — house pattern: route
// read-model types are duplicated, the server JSDoc is the contract.
export interface FailedTokenAffectedIntegration {
  id: string;
  platform: string;
  accountName: string | null;
}

export interface FailedTokenSummary {
  tokenId: string;
  /** oauth family name, e.g. "google" */
  provider: string;
  /** Server-computed via getProviderLabel — single source with the 31-27 email */
  providerLabel: string;
  providerAccountName: string | null;
  tokenStatus: "refresh_failed" | "revoked";
  affectedCount: number;
  affectedIntegrations: FailedTokenAffectedIntegration[];
}

// Integration health status from GET /api/clients/:clientId/integrations/health
export interface IntegrationHealthResult {
  integrationId: string;
  platform: string;
  accountName: string | null;
  status: "healthy" | "warning" | "error";
  issues: string[];
  recommendedAction: string | null;
  lastSyncAt: number | null;
  syncStatus: string;
  tokenStatus: "healthy" | "expiring_soon" | "expired" | "revoked" | "refresh_failed";
}

// Placeholder for Epic 5 — Report Builder
export interface ClientReport {
  id: string;
  templateName: string | null;
  dateRangeStart: string;
  dateRangeEnd: string;
  status: "pending" | "ready";
  createdAt: string;
  completedAt: string | null;
}

export const TAG_COLORS = [
  { value: "#EF4444", label: "Red" },
  { value: "#F97316", label: "Orange" },
  { value: "#EAB308", label: "Yellow" },
  { value: "#22C55E", label: "Green" },
  { value: "#14B8A6", label: "Teal" },
  { value: "#3B82F6", label: "Blue" },
  { value: "#6366F1", label: "Indigo" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#EC4899", label: "Pink" },
  { value: "#F43F5E", label: "Rose" },
  { value: "#64748B", label: "Slate" },
  { value: "#0EA5E9", label: "Sky" },
] as const;
